import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";
import { useAuthStore } from "@/stores/auth";
import { api } from "@/lib/api";

const allPlans = [
  {
    id: "free",
    name: "Free",
    price: "$0",
    description: "Perfect for testing and personal projects",
    features: [
      "1 domain",
      "1,000 messages/month",
      "7-day data retention",
      "Basic analytics",
      "Source intelligence",
      "DNS tools access",
      "Website security scans",
    ],
    cta: "Get started",
    href: "/register",
    popular: false,
    stripePriceId: null,
  },
  {
    id: "starter",
    name: "Starter",
    price: "$19",
    description: "For small businesses and startups",
    features: [
      "5 domains",
      "100,000 messages/month",
      "90-day data retention",
      "Advanced analytics",
      "Source intelligence",
      "Weekly digest emails",
      "Smart recommendations",
      "Website security scans",
      "Email support",
    ],
    cta: "Get started",
    href: "/register",
    popular: false,
    stripePriceId: "starter",
  },
  {
    id: "pro",
    name: "Pro",
    price: "$49",
    description: "For growing companies with multiple domains",
    features: [
      "25 domains",
      "1,000,000 messages/month",
      "1-year data retention",
      "Full analytics suite",
      "Source intelligence",
      "Weekly digest emails",
      "Smart recommendations",
      "Website security scans",
      "API access",
      "Priority support",
      "Team members (5)",
    ],
    cta: "Get started",
    href: "/register",
    popular: true,
    stripePriceId: "pro",
  },
  {
    id: "business",
    name: "Business",
    price: "Custom",
    description: "For enterprises with complex requirements",
    features: [
      "Unlimited domains",
      "Unlimited messages",
      "1-year data retention",
      "Enterprise analytics",
      "Source intelligence",
      "Weekly digest emails",
      "Smart recommendations",
      "Website security scans",
      "Full API access",
      "Dedicated support",
      "Custom integrations",
      "Unlimited team members",
      "SSO/SAML",
      "SLA guarantee",
    ],
    cta: "Contact sales",
    href: "mailto:sales@getsent.dev",
    popular: false,
    stripePriceId: null,
  },
];

// Plan hierarchy for determining upgrades
const planOrder = ["free", "starter", "pro", "business"];

const faqs = [
  {
    question: "What counts as a message?",
    answer:
      "A message is counted based on the email volume reported in DMARC aggregate reports. Each email processed through your domain counts as one message, regardless of whether it passes or fails authentication.",
  },
  {
    question: "Can I change plans later?",
    answer:
      "Yes, you can upgrade or downgrade your plan at any time. When upgrading, you'll be charged the prorated difference. When downgrading, your new rate takes effect at the next billing cycle.",
  },
  {
    question: "Is there a free plan?",
    answer:
      "Yes! Our Free plan is free forever and includes 1 domain with 1,000 messages per month. It's perfect for testing or personal projects. No credit card required.",
  },
  {
    question: "What happens if I exceed my message limit?",
    answer:
      "We'll notify you when you reach 80% of your limit. If you exceed your limit, we'll continue processing reports but some features may be limited until you upgrade or your limit resets.",
  },
  {
    question: "Do you offer annual billing?",
    answer:
      "Yes, we offer annual billing with a 20% discount. Contact us or select annual billing during checkout to take advantage of the savings.",
  },
  {
    question: "Can I get a refund?",
    answer:
      "We offer a 30-day money-back guarantee on all paid plans. If you're not satisfied, contact us within 30 days of your purchase for a full refund.",
  },
];

export function PricingPage() {
  const { user, currentOrganization } = useAuthStore();
  const isLoggedIn = !!user;
  const currentPlan = currentOrganization?.plan || "free";
  const currentPlanIndex = planOrder.indexOf(currentPlan);

  // Filter plans: show only upgrades for logged-in users on free plan
  const plans = isLoggedIn && currentPlan === "free"
    ? allPlans.filter(p => planOrder.indexOf(p.id) > currentPlanIndex)
    : allPlans;

  const handleUpgrade = async (planId: string) => {
    if (!currentOrganization) return;
    try {
      const { url } = await api.billing.createCheckout(currentOrganization.id, planId);
      // Navigate to Stripe checkout - external URL requires location.assign
      window.location.assign(url);
    } catch (error) {
      console.error("Checkout error:", error);
    }
  };

  return (
    <div>
      <SEO
        title="Pricing"
        description="Simple, transparent pricing for DMARC monitoring. Start free and scale as you grow. Plans for individuals, startups, and enterprises."
        canonical="/pricing"
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-10 dark:from-indigo-950/20 sm:py-14">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              {isLoggedIn && currentPlan === "free" ? (
                <>
                  Upgrade to{" "}
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
                    unlock more
                  </span>
                </>
              ) : (
                <>
                  Simple,{" "}
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
                    transparent
                  </span>{" "}
                  pricing
                </>
              )}
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              {isLoggedIn && currentPlan === "free"
                ? "Get more domains, longer data retention, and advanced features."
                : "Start free and scale as you grow. No hidden fees, no surprises."}
            </p>
          </div>
        </div>
      </section>

      {/* Pricing cards */}
      <section className="py-6 sm:py-8">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className={`grid gap-8 justify-center ${
            plans.length === 4 ? "lg:grid-cols-4" :
            plans.length === 3 ? "lg:grid-cols-3 max-w-5xl mx-auto" :
            plans.length === 2 ? "lg:grid-cols-2 max-w-3xl mx-auto" :
            "max-w-md mx-auto"
          }`}>
            {plans.map((plan) => (
              <div key={plan.name} className="flex flex-col">
                {/* Quick upgrade button above card for logged-in free users */}
                {isLoggedIn && currentPlan === "free" && plan.stripePriceId && (
                  <button
                    onClick={() => handleUpgrade(plan.stripePriceId!)}
                    className="mb-10 w-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30 transition-all hover:-translate-y-0.5"
                  >
                    Upgrade to {plan.name}
                  </button>
                )}
                {isLoggedIn && currentPlan === "free" && plan.id === "business" && (
                  <a
                    href="mailto:sales@getsent.dev"
                    className="mb-10 w-full rounded-full bg-gray-900 dark:bg-white px-4 py-2.5 text-sm font-semibold text-white dark:text-gray-900 text-center shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5"
                  >
                    Contact for Business
                  </a>
                )}
                <div
                  className={`relative rounded-2xl border bg-card p-8 shadow-lg flex-1 ${plan.popular
                      ? "border-indigo-500 ring-2 ring-indigo-500"
                      : ""
                    }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                      <span className="rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-1 text-sm font-semibold text-white">
                        Most Popular
                      </span>
                    </div>
                  )}
                <div className="text-center">
                  <h3 className="text-lg font-semibold">{plan.name}</h3>
                  <div className="mt-4">
                    <span className="text-4xl font-bold">{plan.price}</span>
                    {plan.price !== "$0" && plan.price !== "Custom" && (
                      <span className="text-muted-foreground">/month</span>
                    )}
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {plan.description}
                  </p>
                </div>
                <ul className="mt-8 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <svg
                        className="h-5 w-5 shrink-0 text-indigo-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={2}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4.5 12.75l6 6 9-13.5"
                        />
                      </svg>
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.href.startsWith("mailto:") ? (
                  <a
                    href={plan.href}
                    className={`mt-8 block w-full rounded-full py-3 text-center text-sm font-semibold transition-all ${plan.popular
                        ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30"
                        : "border border-border bg-background hover:bg-muted"
                      }`}
                  >
                    {plan.cta}
                  </a>
                ) : isLoggedIn && plan.stripePriceId ? (
                  <button
                    onClick={() => handleUpgrade(plan.stripePriceId!)}
                    className={`mt-8 block w-full rounded-full py-3 text-center text-sm font-semibold transition-all ${plan.popular
                        ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30"
                        : "border border-border bg-background hover:bg-muted"
                      }`}
                  >
                    Upgrade to {plan.name}
                  </button>
                ) : (
                  <Link
                    to={plan.href}
                    className={`mt-8 block w-full rounded-full py-3 text-center text-sm font-semibold transition-all ${plan.popular
                        ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg shadow-indigo-500/25 hover:shadow-xl hover:shadow-indigo-500/30"
                        : "border border-border bg-background hover:bg-muted"
                      }`}
                  >
                    {plan.cta}
                  </Link>
                )}
              </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison table */}
      <section className="border-t py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight">
              Compare all features
            </h2>
          </div>
          <div className="mt-12 overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b">
                  <th className="pb-4 text-left font-semibold">Feature</th>
                  <th className="pb-4 text-center font-semibold">Free</th>
                  <th className="pb-4 text-center font-semibold">Starter</th>
                  <th className="pb-4 text-center font-semibold">Pro</th>
                  <th className="pb-4 text-center font-semibold">Business</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {[
                  ["Domains", "1", "5", "25", "Unlimited"],
                  ["Messages/month", "1K", "100K", "1M", "Unlimited"],
                  ["Data retention", "7 days", "90 days", "1 year", "1 year"],
                  ["Team members", "1", "2", "5", "Unlimited"],
                  ["Source intelligence", true, true, true, true],
                  ["Smart recommendations", true, true, true, true],
                  ["Website security scans", true, true, true, true],
                  ["Weekly digest emails", false, true, true, true],
                  ["API access", false, false, true, true],
                  ["SSO/SAML", false, false, false, true],
                  ["SLA guarantee", false, false, false, true],
                  ["Dedicated support", false, false, false, true],
                ].map(([feature, ...values]) => (
                  <tr key={feature as string}>
                    <td className="py-4 text-sm">{feature}</td>
                    {values.map((value, i) => (
                      <td key={i} className="py-4 text-center text-sm">
                        {typeof value === "boolean" ? (
                          value ? (
                            <svg
                              className="mx-auto h-5 w-5 text-indigo-500"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M4.5 12.75l6 6 9-13.5"
                              />
                            </svg>
                          ) : (
                            <svg
                              className="mx-auto h-5 w-5 text-muted-foreground/30"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={2}
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          )
                        ) : (
                          value
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t bg-muted/30 py-16 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight">
              Frequently asked questions
            </h2>
          </div>
          <div className="mx-auto mt-12 max-w-3xl divide-y">
            {faqs.map((faq) => (
              <div key={faq.question} className="py-6">
                <h3 className="font-semibold">{faq.question}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {!(isLoggedIn && currentPlan === "free") && (
        <section className="py-16 sm:py-24">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="rounded-3xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-16 text-center shadow-2xl sm:px-16">
              <h2 className="text-3xl font-bold text-white sm:text-4xl">
                Ready to get started?
              </h2>
              <p className="mt-4 text-lg text-indigo-100">
                Start monitoring your domains for free. No credit card required.
              </p>
              <Link
                to="/register"
                className="mt-8 inline-flex rounded-full bg-white px-8 py-4 text-base font-semibold text-indigo-600 shadow-lg transition-all hover:bg-indigo-50"
              >
                Create free account
              </Link>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
